<template>
  <div class="custom-table">
    <div class="custom-table-header">
      <Button
        v-if="showCustomColumns"
        class="custom-table-column"
        @click="handleCustomColumnButtonClick"
      >
        自定义列
      </Button>

      <CustomColumn
        v-if="showCustomColumns"
        :baseDataSource="customColumnBaseSource"
        :dataSource="defaultCustomColumns"
        :visible.sync="visible"
        @change="handleCustomColumnChange"
      />

      <!-- <Button
        type="primary"
        @click="handleDownloadButtonClick"
      >
        下载数据（Excel）
      </Button> -->
      <DownloadReport
        v-if="showDownload"
        :topic="topic"
        :args="args"
      />
    </div>

    <DynamicTable
      v-bind="$attrs"
      :summaryTip="summaryTip"
      :columns="displayColumns"
      :dataSource="list"
      :summary="summary"
      :total="total"
      v-on="$listeners"
      @sort-change="handleDynamicTableSortChange"
      @filter-change="handleDynamicTableFilterChange"
    >
      <template v-slot:column="data">
        <slot
          name="column"
          :data="data.data"
        >
          {{ data.data.value || '-' }}
        </slot>
      </template>
    </DynamicTable>
  </div>
</template>

<script>
import { kebabCase, cloneDeep } from 'lodash';
import { Button } from 'element-ui';

import CustomColumn from '@/components/CustomColumnSort2/Index';
import DynamicTable from '@/components/DynamicTableSort/Index';
import DownloadReport from '@/components/DownloadReport/Index';

import * as $cache from '@/common/cache';
import { getFieldsByGroup, getFieldsByBranches } from '@/config/columns/';
import { isEmpty } from '@/common/util/';

// 生成本地缓存 key
// const LOCAL_STORAGE_KEY = `CRM-${kebabCase(location.pathname).toUpperCase()}`;

export default {
  components: {
    Button,

    CustomColumn,
    DynamicTable,
    DownloadReport,
  },

  props: {
    // 自定义列
    showCustomColumns: {
      type: Boolean,
      default() {
        return true;
      },
    },
    spliceIndex: {
      type: Number,
      default() {
        return -1;
      },
    },

    showDownload: {
      type: Boolean,
      default() {
        return true;
      },
    },

    customColumns: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },

    // 固定列
    fixedColumns: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },

    // 原始数据
    dataSource: {
      type: Object,
      required: true,
      default() {
        return {};
      },
    },
    // 下载报表类型
    topic: {
      type: String,
      default: '',
    },

    // 下载报表参数
    args: {
      type: Object,
      default() {
        return {};
      },
    },
    summaryTip: {
      type: String,
      default: '',
    },

     // 用户信息
    userInfo: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  data() {
    return {
      visible: false,

      // 默认展示的自定义列信息
      defaultCustomColumns: this.getDefaultCustomColumns(),

      // 默认展示的固定列信息
      defaultFixedColumns: getFieldsByGroup(this.fixedColumns),
    };
  },

  computed: {
    // 全部列
    columns() {
      const fixed = this.defaultFixedColumns;
      const custom = this.defaultCustomColumns;
      const _spliceIndex =
        this.spliceIndex === -1 ? fixed.length : this.spliceIndex;

      return [
        ...fixed.slice(0, _spliceIndex),
        ...custom,
        ...fixed.slice(_spliceIndex),
      ];
    },

    // 展示用的列
    displayColumns() {
      return this.columns
      // return getFieldsByBranches(this.columns);
    },

    fields() {
      return this.getRequestFields(this.columns);
    },

    customColumnBaseSource() {
      return getFieldsByGroup(this.customColumns);
    },

    list() {
      const { list } = this.dataSource;
      return isEmpty(list) || [];
    },

    summary() {
      const { summary } = this.dataSource;
      return isEmpty(summary) || {};
    },

    total() {
      return this.dataSource.total || 0;
    },
  },

  created() {
    this.$emit('created', this.fields);
  },

  methods: {
    createLocalStorageKey() {

      let extra = `${(this.userInfo || {}).id}_${((this.userInfo || {}).role_ids || []).join('*')}`
      return `CRM-${extra}-${kebabCase(location.pathname).toUpperCase()}`;
    },

    // 点击自定义列按钮
    handleCustomColumnButtonClick() {
      this.visible = true;
    },

    // 修改自定义列
    handleCustomColumnChange(options) {
      const key = this.createLocalStorageKey();
      $cache.localSet(key, options);
      this.defaultCustomColumns = options;
      this.$emit('change-columns', this.fields);
    },

    // 下载数据
    // handleDownloadButtonClick() {
    //   this.$emit('download');
    // },

    // 更改排序
    handleDynamicTableSortChange(sort) {
      this.$emit('sort-change', sort);
    },

    // 更改过滤
    handleDynamicTableFilterChange(filter) {
      this.$emit('filter-x-change', filter);
    },

    // 获取本地缓存的列信息
    getLocalStorageFields() {
      const key = this.createLocalStorageKey();
      return $cache.localGet(key);
    },

    getDefaultCustomColumns() {
      const localFields = this.getLocalStorageFields();

      if (Array.isArray(localFields)) {
        return this.normalizeSelectedColumns(localFields);
      }

      return this.getInitialSelectedColumns();
    },

    getInitialSelectedColumns() {
      return this.flattenSelectedColumns(getFieldsByGroup(this.customColumns));
    },

    isFlatSelectedColumns(list) {
      return (
        Array.isArray(list) &&
        list.some((item) => !Array.isArray(item.children))
      );
    },

    normalizeSelectedColumns(list) {
      if (!Array.isArray(list)) {
        return [];
      }

      if (this.isFlatSelectedColumns(list)) {
        return cloneDeep(list)
          .filter((column) => column.isSelected !== false)
          .map((column, index) => ({
            ...column,
            isSelected: true,
            selectedOrder: this.getSelectedColumnOrder(column, index),
          }))
          .sort((a, b) => a.selectedOrder - b.selectedOrder);
      }

      return this.flattenSelectedColumns(list);
    },

    flattenSelectedColumns(list) {
      const selectedColumns = [];
      let flatIndex = 0;

      (Array.isArray(list) ? list : []).forEach((option) => {
        const children = Array.isArray(option.children) ? option.children : [];

        children.forEach((column) => {
          if (column.isSelected) {
            selectedColumns.push({
              ...cloneDeep(column),
              groupKey: option.key,
              groupName: option.name,
              isSelected: true,
              selectedOrder: this.getSelectedColumnOrder(column, flatIndex),
              __flatIndex: flatIndex,
            });
          }

          flatIndex += 1;
        });
      });

      return selectedColumns
        .sort((a, b) => {
          if (a.selectedOrder !== b.selectedOrder) {
            return a.selectedOrder - b.selectedOrder;
          }

          return a.__flatIndex - b.__flatIndex;
        })
        .map((column) => {
          delete column.__flatIndex;
          return column;
        });
    },

    // 获取接口请求用的参数
    getRequestFields(list) {
      return list.reduce((prev, current) => {
        if (current.children) {
          current.children.forEach((item) => {
            prev.push(item.key);
          });
        } else {
          prev.push(current.key);
        }
        return prev;
      }, []);
    },

    getSelectedColumnOrder(column, fallbackIndex) {
      const order = Number(column.selectedOrder);
      return Number.isFinite(order) ? order : fallbackIndex;
    },
  },
};
</script>

<style lang="less" scoped>
.custom-table {
  &-header {
    float: right;
    margin-bottom: 20px;
  }

  &-column {
    margin-right: 20px;
  }
}
</style>
